/**
 * Agent character pixel data — palette-indexed.
 *
 * Characters are 32x32, viewed from isometric SE angle.
 * They sit at a desk facing towards the camera.
 *
 * Palette indices (from palette.ts):
 *   0 = transparent
 *   1 = primary body (backend color)
 *   2 = secondary body (darker)
 *   3 = skin
 *   4 = skin shadow
 *   5 = hair
 *   6 = outline
 *   7 = white/highlight
 *   8 = monitor glow
 *  15 = error red
 */

import type { SpriteDefinition, PixelFrame } from "../SpriteSheet.js";

const W = 32;
const H = 32;

// Helper: set pixel at (x, y) in a W*H array
function px(pixels: number[], x: number, y: number, color: number): void {
  if (x >= 0 && x < W && y >= 0 && y < H) {
    pixels[y * W + x] = color;
  }
}

// Helper: fill a rectangle
function rect(pixels: number[], x: number, y: number, w: number, h: number, color: number): void {
  for (let dy = 0; dy < h; dy++) {
    for (let dx = 0; dx < w; dx++) {
      px(pixels, x + dx, y + dy, color);
    }
  }
}

// Helper: draw an outline rectangle (1px border)
function outlineRect(pixels: number[], x: number, y: number, w: number, h: number, color: number): void {
  for (let dx = 0; dx < w; dx++) {
    px(pixels, x + dx, y, color);
    px(pixels, x + dx, y + h - 1, color);
  }
  for (let dy = 0; dy < h; dy++) {
    px(pixels, x, y + dy, color);
    px(pixels, x + w - 1, y + dy, color);
  }
}

/**
 * Draw the base character body (no arms — those vary per frame).
 * Character sits facing SE (toward camera).
 */
function drawCharacterBase(pixels: number[]): void {
  // ---- CHAIR (behind character) ----
  // Chair back (visible above shoulders on left side)
  rect(pixels, 10, 10, 2, 8, 6); // left post
  rect(pixels, 20, 10, 2, 8, 6); // right post
  rect(pixels, 10, 10, 12, 2, 6); // top bar

  // ---- HAIR (top of head) ----
  rect(pixels, 13, 6, 6, 3, 5);
  px(pixels, 12, 7, 5);
  px(pixels, 19, 7, 5);

  // ---- HEAD ----
  // Main head shape (8x7)
  rect(pixels, 12, 8, 8, 7, 3); // skin
  // Shadow on right side of face
  rect(pixels, 18, 9, 2, 5, 4);
  // Outline top of head
  for (let x = 12; x < 20; x++) px(pixels, x, 8, 6);
  // Outline sides
  for (let y = 8; y < 15; y++) {
    px(pixels, 11, y, 6);
    px(pixels, 20, y, 6);
  }
  // Chin outline
  for (let x = 12; x < 20; x++) px(pixels, x, 15, 6);

  // ---- EYES ----
  px(pixels, 14, 11, 6); // left eye
  px(pixels, 17, 11, 6); // right eye

  // ---- MOUTH (small line) ----
  px(pixels, 15, 13, 4);
  px(pixels, 16, 13, 4);

  // ---- NECK ----
  rect(pixels, 14, 15, 4, 2, 3);

  // ---- TORSO (shirt / body) ----
  rect(pixels, 11, 17, 10, 7, 1); // main body primary
  rect(pixels, 12, 17, 8, 1, 2);  // collar darker
  // Shadow on right side of torso
  rect(pixels, 19, 18, 2, 5, 2);
  // Outline torso
  for (let y = 17; y < 24; y++) {
    px(pixels, 10, y, 6);
    px(pixels, 21, y, 6);
  }
  for (let x = 10; x < 22; x++) px(pixels, x, 24, 6);
}

/**
 * Draw hands/arms on a desk surface.
 * The desk top edge is at y=24. Arms rest at y=22-24.
 */
function drawArmsOnDesk(pixels: number[], leftUp: boolean, rightUp: boolean): void {
  if (leftUp) {
    // Left arm raised (typing)
    rect(pixels, 9, 20, 2, 3, 3);  // forearm
    px(pixels, 9, 20, 4);
  } else {
    // Left arm resting on desk
    rect(pixels, 8, 22, 3, 2, 3);
    px(pixels, 8, 23, 4);
  }

  if (rightUp) {
    // Right arm raised
    rect(pixels, 21, 20, 2, 3, 3);
    px(pixels, 22, 20, 4);
  } else {
    // Right arm resting on desk
    rect(pixels, 21, 22, 3, 2, 3);
    px(pixels, 23, 23, 4);
  }
}

/**
 * Draw a simple desk surface at the bottom of the frame.
 * The desk occupies roughly y=24 to y=30.
 */
function drawDesk(pixels: number[]): void {
  // Desk top surface (isometric slab)
  rect(pixels, 4, 24, 24, 3, 9);   // main desk surface
  rect(pixels, 4, 24, 24, 1, 11);  // highlight top edge
  rect(pixels, 4, 27, 24, 1, 10);  // shadow bottom edge

  // Desk front face
  rect(pixels, 4, 27, 24, 4, 10);
  rect(pixels, 5, 28, 22, 2, 9);

  // Desk edge outline
  outlineRect(pixels, 3, 24, 26, 8, 6);
}

/**
 * Draw a monitor on the desk.
 * @param glowing Whether the screen should be lit up (palette 8 vs dark)
 * @param errorState Whether to show red screen
 */
function drawMonitor(pixels: number[], glowing: boolean, errorState = false): void {
  // Monitor housing
  rect(pixels, 14, 20, 8, 5, 6);   // frame
  // Screen
  const screenColor = errorState ? 15 : glowing ? 8 : 6;
  rect(pixels, 15, 21, 6, 3, screenColor);
  // Stand
  rect(pixels, 17, 25, 2, 1, 14);
}

// ==================================================================
// IDLE animation — 4 frames, 1.5s cycle
// ==================================================================

function makeIdleFrame0(): PixelFrame {
  const pixels = new Array<number>(W * H).fill(0);
  drawCharacterBase(pixels);
  drawDesk(pixels);
  drawMonitor(pixels, false);
  drawArmsOnDesk(pixels, false, false);
  return { width: W, height: H, pixels };
}

function makeIdleFrame1(): PixelFrame {
  const pixels = new Array<number>(W * H).fill(0);
  drawCharacterBase(pixels);
  drawDesk(pixels);
  drawMonitor(pixels, false);
  drawArmsOnDesk(pixels, false, false);
  // Slight lean back — shift head up 1px visually by darkening bottom row of head
  px(pixels, 14, 14, 3);
  px(pixels, 15, 14, 3);
  return { width: W, height: H, pixels };
}

function makeIdleFrame2(): PixelFrame {
  // Same as frame 0
  return makeIdleFrame0();
}

function makeIdleFrame3(): PixelFrame {
  const pixels = new Array<number>(W * H).fill(0);
  drawCharacterBase(pixels);
  drawDesk(pixels);
  drawMonitor(pixels, false);
  drawArmsOnDesk(pixels, false, false);
  // Head tilt — shift eyes slightly left
  px(pixels, 14, 11, 3); // clear old left eye
  px(pixels, 17, 11, 3); // clear old right eye
  px(pixels, 13, 11, 6); // new left eye (shifted left)
  px(pixels, 16, 11, 6); // new right eye (shifted left)
  return { width: W, height: H, pixels };
}

// ==================================================================
// RUNNING animation — 6 frames, 0.8s cycle
// ==================================================================

function makeRunningFrame0(): PixelFrame {
  const pixels = new Array<number>(W * H).fill(0);
  drawCharacterBase(pixels);
  drawDesk(pixels);
  drawMonitor(pixels, true);
  drawArmsOnDesk(pixels, false, false);
  return { width: W, height: H, pixels };
}

function makeRunningFrame1(): PixelFrame {
  const pixels = new Array<number>(W * H).fill(0);
  drawCharacterBase(pixels);
  drawDesk(pixels);
  drawMonitor(pixels, true);
  drawArmsOnDesk(pixels, true, false); // left hand typing
  return { width: W, height: H, pixels };
}

function makeRunningFrame2(): PixelFrame {
  const pixels = new Array<number>(W * H).fill(0);
  drawCharacterBase(pixels);
  drawDesk(pixels);
  drawMonitor(pixels, true);
  drawArmsOnDesk(pixels, false, true); // right hand typing
  return { width: W, height: H, pixels };
}

function makeRunningFrame3(): PixelFrame {
  const pixels = new Array<number>(W * H).fill(0);
  drawCharacterBase(pixels);
  drawDesk(pixels);
  drawMonitor(pixels, true);
  drawArmsOnDesk(pixels, false, false); // both down
  return { width: W, height: H, pixels };
}

function makeRunningFrame4(): PixelFrame {
  const pixels = new Array<number>(W * H).fill(0);
  drawCharacterBase(pixels);
  drawDesk(pixels);
  drawMonitor(pixels, true);
  drawArmsOnDesk(pixels, true, false); // left typing again
  return { width: W, height: H, pixels };
}

function makeRunningFrame5(): PixelFrame {
  const pixels = new Array<number>(W * H).fill(0);
  drawCharacterBase(pixels);
  drawDesk(pixels);
  drawMonitor(pixels, true);
  drawArmsOnDesk(pixels, false, true); // right typing
  // Lean forward slightly — darken hair top to simulate movement
  rect(pixels, 13, 5, 6, 1, 5);
  return { width: W, height: H, pixels };
}

// ==================================================================
// WAITING animation — 6 frames, 2s cycle
// ==================================================================

function makeWaitingFrame0(): PixelFrame {
  const pixels = new Array<number>(W * H).fill(0);
  drawCharacterBase(pixels);
  drawDesk(pixels);
  drawMonitor(pixels, false);
  // Arms folded on desk
  rect(pixels, 9, 22, 14, 2, 3);
  rect(pixels, 10, 22, 12, 1, 1); // shirt sleeve visible over arms
  return { width: W, height: H, pixels };
}

function makeWaitingFrame1(): PixelFrame {
  const pixels = new Array<number>(W * H).fill(0);
  drawCharacterBase(pixels);
  drawDesk(pixels);
  drawMonitor(pixels, false);
  rect(pixels, 9, 22, 14, 2, 3);
  rect(pixels, 10, 22, 12, 1, 1);
  // Head turns left — eyes shift
  px(pixels, 14, 11, 3);
  px(pixels, 17, 11, 3);
  px(pixels, 13, 11, 6);
  px(pixels, 16, 11, 6);
  return { width: W, height: H, pixels };
}

function makeWaitingFrame2(): PixelFrame {
  const pixels = new Array<number>(W * H).fill(0);
  drawCharacterBase(pixels);
  drawDesk(pixels);
  drawMonitor(pixels, false);
  rect(pixels, 9, 22, 14, 2, 3);
  rect(pixels, 10, 22, 12, 1, 1);
  // Head turns right — eyes shift
  px(pixels, 14, 11, 3);
  px(pixels, 17, 11, 3);
  px(pixels, 15, 11, 6);
  px(pixels, 18, 11, 6);
  return { width: W, height: H, pixels };
}

function makeWaitingFrame3(): PixelFrame {
  const pixels = new Array<number>(W * H).fill(0);
  drawCharacterBase(pixels);
  drawDesk(pixels);
  drawMonitor(pixels, false);
  // Looking at watch — left arm raised
  rect(pixels, 8, 18, 3, 4, 3);
  px(pixels, 8, 18, 1); // sleeve
  px(pixels, 9, 18, 7); // watch glint
  // Right arm resting
  rect(pixels, 21, 22, 3, 2, 3);
  return { width: W, height: H, pixels };
}

function makeWaitingFrame4(): PixelFrame {
  const pixels = new Array<number>(W * H).fill(0);
  drawCharacterBase(pixels);
  drawDesk(pixels);
  drawMonitor(pixels, false);
  // Tapping desk — right hand up
  rect(pixels, 9, 22, 5, 2, 3);
  rect(pixels, 21, 21, 3, 2, 3); // hand slightly raised
  px(pixels, 22, 20, 3); // fingertip
  return { width: W, height: H, pixels };
}

function makeWaitingFrame5(): PixelFrame {
  const pixels = new Array<number>(W * H).fill(0);
  drawCharacterBase(pixels);
  drawDesk(pixels);
  drawMonitor(pixels, false);
  // Tapping desk — right hand down
  rect(pixels, 9, 22, 5, 2, 3);
  rect(pixels, 21, 22, 3, 2, 3); // hand on desk
  return { width: W, height: H, pixels };
}

// ==================================================================
// ERROR animation — 4 frames, 0.6s cycle
// ==================================================================

function makeErrorFrame0(): PixelFrame {
  const pixels = new Array<number>(W * H).fill(0);
  drawCharacterBase(pixels);
  drawDesk(pixels);
  drawMonitor(pixels, false, true); // error screen
  // Hands up alarmed
  rect(pixels, 7, 17, 3, 4, 3);
  rect(pixels, 22, 17, 3, 4, 3);
  return { width: W, height: H, pixels };
}

function makeErrorFrame1(): PixelFrame {
  const pixels = new Array<number>(W * H).fill(0);
  drawCharacterBase(pixels);
  // Red face tint
  rect(pixels, 13, 9, 6, 5, 15);
  // Re-draw eyes on top
  px(pixels, 14, 11, 6);
  px(pixels, 17, 11, 6);
  drawDesk(pixels);
  drawMonitor(pixels, false, true);
  rect(pixels, 7, 17, 3, 4, 3);
  rect(pixels, 22, 17, 3, 4, 3);
  return { width: W, height: H, pixels };
}

function makeErrorFrame2(): PixelFrame {
  const pixels = new Array<number>(W * H).fill(0);
  drawCharacterBase(pixels);
  drawDesk(pixels);
  drawMonitor(pixels, false, true);
  // Hands on head
  rect(pixels, 10, 7, 3, 3, 3);
  rect(pixels, 19, 7, 3, 3, 3);
  return { width: W, height: H, pixels };
}

function makeErrorFrame3(): PixelFrame {
  const pixels = new Array<number>(W * H).fill(0);
  drawCharacterBase(pixels);
  drawDesk(pixels);
  drawMonitor(pixels, false, true);
  // Leaning back distressed
  rect(pixels, 7, 18, 3, 3, 3);
  rect(pixels, 22, 18, 3, 3, 3);
  // Head shifted back slightly (redraw eyes higher)
  px(pixels, 14, 10, 6);
  px(pixels, 17, 10, 6);
  px(pixels, 14, 11, 3);
  px(pixels, 17, 11, 3);
  return { width: W, height: H, pixels };
}

// ==================================================================
// TERMINATED — 1 frame (static, desaturated drawn by dimming in renderer)
// ==================================================================

function makeTerminatedFrame(): PixelFrame {
  const pixels = new Array<number>(W * H).fill(0);
  drawCharacterBase(pixels);
  drawDesk(pixels);
  drawMonitor(pixels, false);
  // Slumped forward — head drops to desk level
  // Overwrite head area with hair/desk to simulate slump
  rect(pixels, 12, 14, 8, 4, 5); // hair visible, face hidden
  rect(pixels, 13, 18, 6, 4, 1); // torso compressed
  // Arms flat on desk
  rect(pixels, 8, 23, 16, 2, 3);
  return { width: W, height: H, pixels };
}

// ==================================================================
// EXPORTED SPRITE DEFINITIONS
// ==================================================================

export const CHARACTER_IDLE: SpriteDefinition = {
  name: "character-idle",
  frames: [makeIdleFrame0(), makeIdleFrame1(), makeIdleFrame2(), makeIdleFrame3()],
  originX: W / 2,
  originY: H - 8, // anchor near bottom, above desk edge
};

export const CHARACTER_RUNNING: SpriteDefinition = {
  name: "character-running",
  frames: [
    makeRunningFrame0(), makeRunningFrame1(), makeRunningFrame2(),
    makeRunningFrame3(), makeRunningFrame4(), makeRunningFrame5(),
  ],
  originX: W / 2,
  originY: H - 8,
};

export const CHARACTER_WAITING: SpriteDefinition = {
  name: "character-waiting",
  frames: [
    makeWaitingFrame0(), makeWaitingFrame1(), makeWaitingFrame2(),
    makeWaitingFrame3(), makeWaitingFrame4(), makeWaitingFrame5(),
  ],
  originX: W / 2,
  originY: H - 8,
};

export const CHARACTER_ERROR: SpriteDefinition = {
  name: "character-error",
  frames: [makeErrorFrame0(), makeErrorFrame1(), makeErrorFrame2(), makeErrorFrame3()],
  originX: W / 2,
  originY: H - 8,
};

export const CHARACTER_TERMINATED: SpriteDefinition = {
  name: "character-terminated",
  frames: [makeTerminatedFrame()],
  originX: W / 2,
  originY: H - 8,
};

/** All character animation definitions. */
export const ALL_CHARACTER_DEFS = {
  idle: CHARACTER_IDLE,
  running: CHARACTER_RUNNING,
  waiting: CHARACTER_WAITING,
  error: CHARACTER_ERROR,
  terminated: CHARACTER_TERMINATED,
} as const;
